/**
 * ScoreHistogramChart — distribution of risk scores across the active
 * window, bucketed in tens. Threshold lines at 30/60/80 mark the
 * watch / suspicious / high band edges.
 */
import { Bar, BarChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis, Cell } from "recharts";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useFilters } from "@/hooks/useFilters";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { Card, CardHeader, EmptyState, ErrorState, Skeleton } from "@/components/primitives";
import { fmtNumber } from "@/lib/format";

const colorForBucket = (b: number) =>
  b >= 80 ? "rgb(var(--sev-high))" :
  b >= 60 ? "rgb(var(--sev-suspicious))" :
  b >= 30 ? "rgb(var(--sev-watch))" :
            "rgb(var(--sev-good))";

export function ScoreHistogramChart() {
  const { filters } = useFilters();
  const { tick } = useAutoRefresh();
  const q = useQuery({
    queryKey: ["score-histogram", filters, tick],
    queryFn: ({ signal }) => api.scoreHistogram(filters, signal),
  });

  // Ensure all 10 buckets always render (zero-fill missing).
  const data = (() => {
    const map = new Map<number, number>();
    for (const r of q.data?.data ?? []) map.set(r.bucket, r.count);
    return Array.from({ length: 10 }, (_, i) => ({ bucket: i * 10, count: map.get(i * 10) ?? 0 }));
  })();

  return (
    <Card>
      <CardHeader
        title="Score distribution"
        subtitle="How many sampled events fall into each 10-point band. Reference lines mark the watch / suspicious / high thresholds."
      />
      {q.isLoading ? (
        <Skeleton className="h-72" />
      ) : q.error ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : !data.some((d) => d.count > 0) ? (
        <EmptyState title="No events in this window." />
      ) : (
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 10, right: 16, bottom: 0, left: 0 }}>
              <CartesianGrid stroke="rgb(var(--border))" strokeDasharray="2 4" />
              <XAxis
                dataKey="bucket"
                stroke="rgb(var(--fg-muted))"
                tick={{ fontSize: 11 }}
                tickFormatter={(v: number) => `${v}\u2013${v + 9}`}
              />
              <YAxis
                stroke="rgb(var(--fg-muted))"
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => fmtNumber(v, { compact: true })}
              />
              <Tooltip
                cursor={{ fill: "rgb(var(--surface-3))" }}
                contentStyle={{ background: "rgb(var(--surface-1))", border: "1px solid rgb(var(--border))", fontSize: 12 }}
                labelFormatter={(v: number) => `Score ${v}\u2013${v + 9}`}
                formatter={(v: number) => [fmtNumber(v), "Events"]}
              />
              <ReferenceLine x={30} stroke="rgb(var(--sev-watch))"      strokeDasharray="3 3" />
              <ReferenceLine x={60} stroke="rgb(var(--sev-suspicious))" strokeDasharray="3 3" />
              <ReferenceLine x={80} stroke="rgb(var(--sev-high))"       strokeDasharray="3 3" />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {data.map((d) => (
                  <Cell key={d.bucket} fill={colorForBucket(d.bucket)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}
